import React, { useContext } from "react";
import { AppContext } from "../../../context/AppContext";
import fileIcon from "../../../assets/images/fileIcon.svg";

const IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$|\/)/i;
const VIDEO_RE = /\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$|\/)/i;
const AUDIO_RE = /\.(mp3|m4a|wav|ogg|aac)(\?|#|$|\/)/i;

const getFileMeta = (url) => {
  if (!url || typeof url !== "string") return { name: "Document", ext: "FILE" };
  const cleanPath = url.split("?")[0].split("#")[0];
  let name = cleanPath.split("/").pop() || "Document";
  try {
    name = decodeURIComponent(name);
  } catch (_) {
    // leave the raw segment if decoding fails
  }
  const dotIdx = name.lastIndexOf(".");
  const ext = dotIdx >= 0 ? name.slice(dotIdx + 1).toUpperCase() : "FILE";
  return { name, ext };
};

// `newMessages` items carry attachments as `files: string[]`; `conversations`
// items carry a single `Resource_Path: string`. The reply target can be either
// shape, so probe both before deciding the media kind.
const detectMedia = (replyTo) => {
  const url =
    (Array.isArray(replyTo.files) && replyTo.files[0]) ||
    replyTo.Resource_Path ||
    null;
  if (!url || typeof url !== "string") return null;
  if (IMAGE_RE.test(url)) return { kind: "image", url };
  if (VIDEO_RE.test(url)) return { kind: "video", url };
  const meta = getFileMeta(url);
  if (AUDIO_RE.test(url)) return { kind: "audio", url, ...meta };
  return { kind: "file", url, ...meta };
};

// Compact strip surfaced above the composer when a user is composing a reply.
// The "x" closes the reply state by clearing it via setReplyTo({}) — same
// reset Composer.js performs after a successful send.
const ReplyBanner = ({ replyTo, setReplyTo, msgRef }) => {
  const { conversations, newMessages } = useContext(AppContext);

  if (!(replyTo.Team_Message_Id || replyTo.message_id)) return null;

  const media = detectMedia(replyTo);
  const textBody = replyTo.Team_Message_Text || replyTo.message || "";
  let previewText = textBody;
  if (!previewText && media) {
    if (media.kind === "image") previewText = "Photo";
    else if (media.kind === "video") previewText = "Video";
    else previewText = media.name || (media.kind === "audio" ? "Audio" : "File");
  }
  const senderLabel =
    replyTo.sender_name ||
    [replyTo.FirstName, replyTo.LastName].filter(Boolean).join(" ") ||
    "Reply";

  const scrollToOriginal = () => {
    const msgIndex = conversations
      .map((item) => item.Team_Message_Id)
      .indexOf(replyTo.Team_Message_Id);
    const reference =
      msgIndex !== -1
        ? msgRef.current[msgIndex]
        : msgRef[conversations.length + newMessages.indexOf(replyTo)];
    reference && reference.scrollIntoView && reference.scrollIntoView();
  };

  const renderThumb = () => {
    if (!media) return null;
    if (media.kind === "image") {
      return (
        <span className="comm-chatbox__reply-banner-thumb">
          <img src={media.url} alt="" />
        </span>
      );
    }
    if (media.kind === "video") {
      return (
        <span className="comm-chatbox__reply-banner-thumb">
          <video src={media.url} muted preload="metadata" playsInline />
        </span>
      );
    }
    return (
      <span className="comm-chatbox__reply-banner-thumb comm-chatbox__reply-banner-thumb--file">
        <img src={fileIcon} alt="" />
      </span>
    );
  };

  return (
    <div className="comm-chatbox__reply-banner" role="status">
      {renderThumb()}
      <div
        className="comm-chatbox__reply-banner-body"
        onClick={scrollToOriginal}
      >
        <span className="comm-chatbox__reply-banner-label">
          Replying to {senderLabel}
        </span>
        <span className="comm-chatbox__reply-banner-preview">
          {previewText}
        </span>
      </div>
      {setReplyTo && (
        <button
          type="button"
          className="comm-chatbox__reply-banner-close"
          aria-label="Cancel reply"
          onClick={() => setReplyTo({})}
        >
          ×
        </button>
      )}
    </div>
  );
};

export default ReplyBanner;
